/**
 * 테이블 정렬 - useTableSort 훅 + SortableTh 헤더 칸 (공통 .table-wrap 테이블용)
 *
 *   const sort = useTableSort<'name' | 'createdAt'>();
 *   const rows = sort.sortRows(list, (row, key) => row[key] ?? '');
 *   <th><SortableTh sort={sort} col="name">이름</SortableTh></th>
 *
 * - 같은 칸 다시 클릭 = 오름차순 ↔ 내림차순, 다른 칸 클릭 = 그 칸 오름차순
 * - 정렬 칸을 고르기 전(sortKey = null)에는 원래 순서 그대로
 * - 아이콘 스타일: index.css "Table" 섹션 .table-sort-icon (워크플로우 목록과 동일)
 */

import { ReactNode, useState } from 'react';
import SortArrowsIcon from '../../assets/icons/common/sort.svg?react';

export type SortDir = 'asc' | 'desc';

export interface TableSort<K extends string> {
  sortKey: K | null;
  sortDir: SortDir;
  toggleSort: (key: K) => void;
  sortRows: <T>(rows: T[], getValue: (row: T, key: K) => string | number) => T[];
}

export function useTableSort<K extends string>(initialKey: K | null = null, initialDir: SortDir = 'asc'): TableSort<K> {
  const [sortKey, setSortKey] = useState<K | null>(initialKey);
  const [sortDir, setSortDir] = useState<SortDir>(initialDir);

  const toggleSort = (key: K) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortRows = <T,>(rows: T[], getValue: (row: T, key: K) => string | number) => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const av = getValue(a, sortKey);
      const bv = getValue(b, sortKey);
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), 'ko');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  };

  return { sortKey, sortDir, toggleSort, sortRows };
}

interface SortableThProps<K extends string> {
  sort: TableSort<K>;
  col: K;
  children: ReactNode;
}

/** <th> 안에 넣는 클릭 영역 (글자 + ↓↑ 아이콘) */
export function SortableTh<K extends string>({ sort, col, children }: SortableThProps<K>) {
  const active = sort.sortKey === col;
  return (
    <button type="button" className="table-sort-btn" onClick={() => sort.toggleSort(col)}>
      {children}
      <span className={`table-sort-icon${active ? ` active ${sort.sortDir}` : ''}`}>
        <SortArrowsIcon />
      </span>
    </button>
  );
}

export default SortableTh;
